import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AppModal from '../../components/AppModal/AppModal';
import BucketsLoading from '../BucketManagerNew/components/BucketsLoading';
import useBucketData, { orderClientsForPicker } from '../BucketManagerNew/hooks/useBucketData';
import { activeSuggestions, chipColor, initials } from '../BucketManagerNew/constants';
import useExclusionConfirm from './useExclusionConfirm';
import ExclusionWarningModal from './ExclusionWarningModal';
import { api } from '../../app/http-common';
import '../BucketManagerNew/BucketManagerNew.css';

/**
 * The Stories screen's explicit add control: pick a bucket, then add.
 *
 * WHY IT EXISTS. Dragging a story card onto the Publisher Buckets panel was
 * the only way to place a story from this screen, and a drag is the one
 * gesture that cannot be done deliberately with a trackpad on a long list.
 * Orr, 2026-11-01. The drag is unchanged, this sits beside it.
 *
 * IT IS THE SAME PICKER AS "Send to another bucket" (MoveToModal), down to the
 * publisher ordering, which both now take from `orderClientsForPicker`. What
 * differs is only what the destination means: there the story is already in a
 * bucket and the question is move or copy, here it is in none and there is one
 * action. So no mode toggle, and the footer names the single thing it will do.
 *
 * NOTHING IS WRITTEN UNTIL THE FOOTER IS PRESSED. The modal this replaced
 * fired the add on the `change` event of a <select>, so a mis-aimed click
 * placed a story in a client's list with no confirmation and no undo. A
 * destination is now a selection, and the add is a separate press.
 *
 * REFUSALS COME FROM THE SERVER, VERBATIM. Two write gates can refuse a
 * manual add: the language/country exclusion gate and the single-publisher
 * rule, both live in production since 1 Nov 2026. The 422 body carries the
 * sentence the gate composed ("all 6 of its articles come from one publisher
 * (theinventory.com)"), so that is what the operator reads. A local copy of
 * the wording would be the copy they read and the one that drifts.
 *
 * THE EXCLUSION CONFIRMATION IS THE SHARED ONE. `useExclusionConfirm` is asked
 * before anything is written, so the two-step override an administrator sees
 * here is the same dialog, in the same order, as on the drag path and in
 * Bucket Stories.
 *
 * ADMIN ONLY, because `POST /publisher-buckets/:id/stories` is
 * `verifySuperUser`. Offering this to an editor would be offering a 403.
 */
export default function AddToBucketModal({
  open, story, collectionScope = null, onClose, showToastNotification,
}) {
  const { clients, loading, reload } = useBucketData();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // A refusal and a duplicate are both shown HERE, not as a toast. The screen's
  // toast auto-hides after three seconds and renders error and success in the
  // same orange, which is no way to read a sentence like "all 6 of its articles
  // come from one publisher (theinventory.com)". The modal also stays open, so
  // the operator can pick a different bucket without starting again.
  const [notice, setNotice] = useState(null); // { tone: 'refused' | 'neutral', text }
  const exclusion = useExclusionConfirm();

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setExpanded(new Set());
    setSelectedBucket(null);
    setSubmitting(false);
    setNotice(null);
  }, [open]);

  const orderedClients = useMemo(() => orderClientsForPicker(clients), [clients]);

  const filteredTree = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orderedClients.map((c) => ({ client: c, matchedBuckets: c.buckets }));
    return orderedClients
      .map((c) => {
        const clientHit = c.clientName.toLowerCase().includes(q);
        const matchedBuckets = clientHit
          ? c.buckets
          : c.buckets.filter((b) => (b.name || '').toLowerCase().includes(q));
        if (!clientHit && matchedBuckets.length === 0) return null;
        return { client: c, matchedBuckets };
      })
      .filter(Boolean);
  }, [orderedClients, query]);

  // Search expands only the publishers it matched, so the rest of the list
  // stays quiet. Same rule as the Send to another bucket picker.
  useEffect(() => {
    if (!query.trim()) return;
    setExpanded(new Set(filteredTree.map((n) => n.client.clientId)));
  }, [query, filteredTree]);

  const toggleClient = useCallback((clientId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
      return next;
    });
  }, []);

  const commit = useCallback(async () => {
    if (!selectedBucket || submitting) return;
    const bucket = selectedBucket;

    // Asked while this modal is still open, so the two confirmations are not
    // orphaned behind a dismissed dialog. Resolves to null when the
    // administrator backs out, or when the bucket enforces the exclusion on
    // every path and there is nothing to override.
    const decision = await exclusion.request({
      bucketId: bucket._id, storyId: story._id, bucketName: bucket.name,
    });
    if (!decision) return;

    setNotice(null);
    setSubmitting(true);
    try {
      await api.addStoryToBucket(bucket._id, story._id, collectionScope, {}, {
        overrideExclusion: decision.override, screen: 'stories_add_to_bucket',
      });
      showToastNotification?.(`Added to ${bucket.name}.`);
      reload();
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      if (status === 409) {
        // A normal outcome, named plainly. The bucket already holds the story,
        // which is not a failure of anything and must not read as one.
        setNotice({ tone: 'neutral', text: `Already in ${bucket.name}.` });
      } else if (status === 422) {
        // The gate's own sentence. See the header note.
        setNotice({
          tone: 'refused',
          text: data.error || 'Story is not eligible for this bucket.',
        });
      } else if (status === 403) {
        setNotice({ tone: 'refused', text: 'You do not have permission to add stories to buckets.' });
      } else {
        console.error('Failed to add story to bucket:', err);
        setNotice({ tone: 'refused', text: 'Could not add the story.' });
      }
      setSubmitting(false);
    }
  }, [selectedBucket, submitting, exclusion, story, collectionScope, showToastNotification, reload, onClose]);

  if (!open) return null;

  return (
    <>
      <AppModal
        isOpen
        title="Add to bucket"
        onCancel={onClose}
        maxWidth={620}
        modalTestId="add-to-bucket-modal"
        footer={(
          <div className="pbnew-move-footer">
            <span className="pbnew-move-note" style={{ fontSize: 12, color: '#3C4A5E' }}>
              Arrives as a Suggestion.
            </span>
            <div className="pbnew-move-footer-right">
              <button type="button" className="pbnew-btn" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="pbnew-btn primary"
                disabled={!selectedBucket || submitting}
                onClick={commit}
              >
                {submitting ? 'Adding...' : `Add to ${selectedBucket ? selectedBucket.name : '...'}`}
              </button>
            </div>
          </div>
        )}
      >
        <div className="pbnew-scope">
          <div style={{ fontSize: 13, color: '#3C4A5E', marginBottom: 10 }}>{story?.title}</div>
          {notice && (
            <div
              role="status"
              data-testid="add-to-bucket-notice"
              data-tone={notice.tone}
              style={notice.tone === 'refused'
                ? {
                  background: '#fff0f0', border: '1px solid #ffcdd2', color: '#d32f2f',
                  borderRadius: 8, padding: '10px 12px', marginBottom: 12,
                  fontSize: 13, lineHeight: 1.5,
                }
                : {
                  background: '#F7F9FC', border: '1px solid rgba(60,74,94,0.12)', color: '#3C4A5E',
                  borderRadius: 8, padding: '10px 12px', marginBottom: 12,
                  fontSize: 13, lineHeight: 1.5,
                }}
            >
              {notice.text}
            </div>
          )}
          <div className="pbnew-search" style={{ maxWidth: 'none', marginBottom: 12 }}>
            <input
              autoFocus
              placeholder="Search publisher or bucket"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="pbnew-filter-label">All available targets</div>
          <div className="pbnew-movetree">
            {filteredTree.map(({ client, matchedBuckets }) => {
              const isOpen = expanded.has(client.clientId);
              return (
                <div key={client.clientId}>
                  <button
                    type="button"
                    className="pbnew-move-client"
                    onClick={() => toggleClient(client.clientId)}
                    aria-expanded={isOpen}
                  >
                    <span className={`pbnew-chev ${isOpen ? 'open' : ''}`}>
                      {isOpen
                        ? <ExpandMoreIcon style={{ fontSize: 16 }} />
                        : <ChevronRightIcon style={{ fontSize: 16 }} />}
                    </span>
                    <span className="pbnew-chip" style={{ background: chipColor(client.clientName) }}>
                      {initials(client.clientName)}
                    </span>
                    <span className="name" style={{ whiteSpace: 'normal' }}>{client.clientName}</span>
                    <span className="info">
                      {matchedBuckets.length} bucket{matchedBuckets.length === 1 ? '' : 's'}
                    </span>
                  </button>
                  {isOpen && matchedBuckets.map((b) => {
                    const selected = selectedBucket?._id === b._id;
                    return (
                      <div key={b._id} className={`pbnew-move-folder ${selected ? 'selected' : ''}`}>
                        <button
                          type="button"
                          className="pbnew-move-folder-btn"
                          title={`Add to ${b.name}`}
                          onClick={() => { setSelectedBucket(b); setNotice(null); }}
                        >
                          <FolderOutlinedIcon style={{ fontSize: 16 }} />
                          <span className="pbnew-move-folder-name">{b.name}</span>
                          <span className="count">{activeSuggestions(b)}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {loading && <BucketsLoading size={120} />}
            {!loading && filteredTree.length === 0 && (
              <div className="pbnew-drop-hint" style={{ padding: 16 }}>No matching destinations.</div>
            )}
          </div>
        </div>
      </AppModal>
      <ExclusionWarningModal {...exclusion.modalProps} />
    </>
  );
}
